import { Controller } from "@hotwired/stimulus"

// Countdown timer controller for displaying time remaining
export default class extends Controller {
  static targets = ["display", "progressBar", "container", "expiredMessage", "resendSection"]
  static values = {
    seconds: Number,
    messagePrefix: { type: String, default: "Code expires in" },
    forceReset: { type: Boolean, default: false }
  }

  connect() {
    this.startTimer()
  }

  // Force reset when the flag changes (after resend)
  forceResetValueChanged() {
    if (this.forceResetValue) {
      this.stopTimer()
      this.resetUI()
      this.startTimer()
    }
  }

  disconnect() {
    this.stopTimer()
  }

  // Automatically called when secondsValue changes (e.g., after Turbo navigation)
  secondsValueChanged() {
    this.stopTimer()
    this.startTimer()
  }

  resetUI() {
    // Show timer container
    if (this.hasContainerTarget) {
      this.containerTarget.classList.toggle("hidden", false)
    }

    // Show resend section
    if (this.hasResendSectionTarget) {
      this.resendSectionTarget.classList.toggle("hidden", false)
    }

    // Hide expired message
    if (this.hasExpiredMessageTarget) {
      this.expiredMessageTarget.classList.toggle("hidden", true)
    }

    // Dispatch reset event for other controllers
    this.dispatch("reset")
  }

  startTimer() {
    // Guard against overlapping intervals
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }

    // Store expiration timestamp for accurate time tracking
    this.expirationTime = Date.now() + (this.secondsValue * 1000)
    this.initialSeconds = this.secondsValue

    // Initial UI update
    this.updateTimerUI()

    // Update UI every second
    this.intervalId = setInterval(() => {
      this.updateTimerUI()
    }, 1000)
  }

  stopTimer() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  }

  updateTimerUI() {
    // Calculate remaining time based on actual timestamp
    const now = Date.now()
    const remainingMs = Math.max(0, this.expirationTime - now)
    const remainingSeconds = Math.ceil(remainingMs / 1000)

    // Check if expired
    if (remainingSeconds <= 0) {
      this.stopTimer()
      this.onExpired()
      return
    }

    // Update display
    this.updateDisplay(remainingSeconds)
    this.updateProgressBar(remainingSeconds)
  }

  updateDisplay(remainingSeconds) {
    if (this.hasDisplayTarget) {
      const minutes = Math.floor(remainingSeconds / 60)
      const seconds = remainingSeconds % 60
      const formatted = `${minutes}:${seconds.toString().padStart(2, '0')}`
      this.displayTarget.textContent = `${this.messagePrefixValue} ${formatted}`
    }
  }

  updateProgressBar(remainingSeconds) {
    if (this.hasProgressBarTarget && this.initialSeconds > 0) {
      const percentage = (remainingSeconds / this.initialSeconds) * 100
      this.progressBarTarget.style.width = `${Math.max(0, percentage)}%`
    }
  }

  showResendSection() {
    if (this.hasResendSectionTarget) {
      this.resendSectionTarget.classList.toggle("hidden", false)
    }
  }

  onExpired() {
    // Hide the timer section
    if (this.hasContainerTarget) {
      this.containerTarget.classList.toggle("hidden", true)
    }

    // Hide the "Didn't get any code?" resend section
    if (this.hasResendSectionTarget) {
      this.resendSectionTarget.classList.toggle("hidden", true)
    }

    // Show the expired message
    if (this.hasExpiredMessageTarget) {
      this.expiredMessageTarget.classList.toggle("hidden", false)
    }

    // Dispatch expired event for other controllers
    this.dispatch("expired")
  }
}

